/**
 * The page of a Dockerfile or compose file pinned in a service (utils/runnablesModel.js), under its head (name, state,
 * Start all / Stop, Logs) on the Apps tab. A compose file lists its services as items, each with its state, Start and
 * Stop of its own and its last log lines (preview-runner `compose_routes.rs`: `up -d <service>`, `stop <service>`,
 * `logs <service>`), read again every few seconds while the stack runs. The stack's own Start (Start all) brings every
 * service up with `docker compose up`, which owns them: one service is started on its own only while the stack runs.
 * A Dockerfile says what it builds and from where.
 * When the stack failed or crashed (`failure`, the Problem box's sentence), its services read "Failed · not created"
 * with that reason, not "Not started". What vfs-service found wrong in the file before any start (a runnable's
 * `problems`, e.g. a mounted file that is not in the project, which blocks the start; `warnings`) is listed on top.
 * A compose file's "Preview entry point" (EntryPicker): which of its services (and container port) the preview, Open app
 * and share links show, from the runner's candidates (`composeEntry` on its status, `entry` on its compose services:
 * `{ candidates: [{ service, port, http, chosen, recommended }], chosen, saved, error }`), the ones that answer HTTP or
 * were not probed yet; saved on the pin (`entry` in `.flow/project.json` runnables), sent with its start, "Restart to
 * apply" while it runs.
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Select } from 'antd';
import {
  composeServiceAction, getComposeLogs, getComposeServices, isRunning,
} from '../../services/SandboxService';
import { Card } from './ServiceTabs/common';
import ProvideMissingFiles from './ProvideMissingFiles';

export const COMPOSE_POLL_MS = 5000;

const STATE_LABELS = { running: 'Running', exited: 'Stopped', created: 'Created', restarting: 'Restarting', paused: 'Paused', dead: 'Stopped', 'not started': 'Not started' };
const stateLabel = (state) => STATE_LABELS[state] || state || 'Not started';
const whatItRuns = (service) => service.image || (service.build ? `built from ${service.build.context || '.'}${service.build.dockerfile ? ` (${service.build.dockerfile})` : ''}` : '');
const folderOf = (file) => (String(file).includes('/') ? String(file).replace(/\/[^/]*$/, '') : '.');

const cleanList = (list) => (Array.isArray(list) ? list : []).map((line) => String(line || '').trim()).filter(Boolean);

/** One provided source, in words: what stands for it and where it is mounted. */
export function providedLine(item) {
  const name = String(item.source || '').split('/').pop();
  if (item.by === 'kept') { return `${name}: ${item.service} gets a folder Flow keeps for it, outside the project (${item.target}).`; }
  if (item.by === 'folder') { return `${name}: its secret files (${(item.names || []).join(', ')}) are mounted at ${item.target} in ${item.service}.`; }
  return `${name}: the secret file ${item.name} is mounted at ${item.target} in ${item.service}.`;
}

/**
 * What vfs-service found in a pinned file before any start: `problems` (they block the start, e.g. a bind-mounted file
 * the project lacks) and `warnings` (e.g. a mounted folder that is missing or empty). Nothing when it found nothing.
 * `missing` (each missing source) adds "Provide missing files" (`onProvide`); `provided` says what Flow mounts in place
 * of a missing one (a secret file, a secret folder, a folder it keeps).
 */
function FileProblems({ problems, warnings, missing = [], provided = [], onProvide = null }) {
  const blocking = cleanList(problems);
  const notes = cleanList(warnings);
  const fixable = onProvide && missing.length > 0;
  const provideButton = fixable ? (
    <Button className="apps-provide-btn" data-testid="apps-provide-missing" onClick={onProvide} size="small" type={blocking.length ? 'primary' : 'default'}>
      {missing.length === 1 ? 'Provide it as a secret file' : `Provide missing files (${missing.length})`}
    </Button>
  ) : null;
  return (
    <>
      {blocking.length > 0 && (
        <div className="apps-runnable-problems" data-testid="apps-runnable-problems" role="note">
          <strong>Before it can start</strong>
          <ul>{blocking.map((line) => <li key={line}>{line}</li>)}</ul>
          {provideButton}
        </div>
      )}
      {notes.length > 0 && (
        <div className="apps-runnable-warnings" data-testid="apps-runnable-warnings" role="note">
          <strong>Worth a look</strong>
          <ul>{notes.map((line) => <li key={line}>{line}</li>)}</ul>
          {!blocking.length && provideButton}
        </div>
      )}
      {provided.filter((item) => item.by !== 'kept').length > 0 && (
        <div className="apps-runnable-provided" data-testid="apps-runnable-provided" role="note">
          <strong>Provided by Flow</strong>
          <ul>{provided.filter((item) => item.by !== 'kept').map((item) => <li key={`${item.service}:${item.source}`}>{providedLine(item)}</li>)}</ul>
        </div>
      )}
    </>
  );
}
FileProblems.propTypes = { missing: PropTypes.array, onProvide: PropTypes.func, problems: PropTypes.array, provided: PropTypes.array, warnings: PropTypes.array };

const entryKey = (entry) => (entry ? `${entry.service}:${entry.port || ''}` : '');
// A saved name alone (a service's `.flow/service.json` `"entry": "web"`): that service, its port the runner's pick.
const entryLabel = (entry) => (entry.port ? `${entry.service} · ${entry.port}` : entry.service);

/**
 * "Preview entry point: envoy · 8080 ▾": the compose service (and its container port) the preview shows. `entry` is the
 * runner's answer (null for a session that is not a compose app or never ran), `saved` the pin's choice. Saving calls
 * `onSave({ service, port })`; while the stack runs a changed choice offers "Restart to apply" (`onApply`).
 */
export function EntryPicker({ entry = null, saved = null, running = false, readOnly = false, onSave = null, onApply = null }) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [changed, setChanged] = useState(false);
  const candidates = (entry?.candidates || []).filter((item) => item && item.http !== false);
  // A saved name alone is the runner's candidate of that service (its chosen one first), when it lists it.
  const named = saved && !saved.port
    ? candidates.find((item) => item.service === saved.service && item.chosen) || candidates.find((item) => item.service === saved.service) || saved
    : saved;
  const current = named || entry?.chosen || candidates.find((item) => item.chosen) || null;
  const http = candidates.filter((item) => item.http === true);
  const options = candidates.map((item) => ({
    value: entryKey(item),
    label: `${entryLabel(item)}${item.recommended ? ' · Recommended' : ''}${item.http === undefined || item.http === null ? ' · not checked yet' : ''}`,
  }));
  // The saved choice even when the runner does not list it (yet): it stays visible.
  if (named && !candidates.some((item) => entryKey(item) === entryKey(named))) { options.unshift({ value: entryKey(named), label: entryLabel(named) }); }
  if (!options.length) { return null; }
  const save = async(value) => {
    const [service, port] = [value.slice(0, value.lastIndexOf(':')), Number(value.slice(value.lastIndexOf(':') + 1))];
    setBusy('save');
    setError(null);
    try {
      await onSave({ service, port });
      setChanged(true);
    } catch (err) {
      setError(err?.message || 'The entry point could not be saved');
    } finally {
      setBusy(null);
    }
  };
  const apply = async() => {
    setBusy('apply');
    setError(null);
    try {
      await onApply();
      setChanged(false);
    } catch (err) {
      setError(err?.message || 'It could not restart');
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="apps-entry" data-testid="apps-entry">
      <label className="apps-entry-row">
        <span>Preview entry point:</span>
        <Select
          aria-label="Preview entry point"
          className="apps-entry-select"
          data-testid="apps-entry-select"
          disabled={readOnly || !onSave || Boolean(busy)}
          onChange={save}
          options={options}
          placeholder="Chosen when the stack starts"
          popupMatchSelectWidth={false}
          size="small"
          value={current ? entryKey(current) : undefined}
        />
        {changed && running && onApply && !readOnly && (
          <Button data-testid="apps-entry-apply" loading={busy === 'apply'} onClick={apply} size="small" type="primary">Restart to apply</Button>
        )}
      </label>
      {error && <p className="apps-bad apps-small" role="alert">{error}</p>}
      {http.length > 1 && (
        <p className="apps-muted apps-small" data-testid="apps-entry-several">
          {http.length} services answer HTTP; the preview, Open app and share links show only the entry point.
        </p>
      )}
      <p className="apps-muted apps-small" data-testid="apps-entry-prefix">
        Some apps do not work under a path prefix (<code>/api/preview/…/proxy/</code>):{' '}
        <button className="apps-info-link" onClick={() => document.querySelector('.share-top-btn')?.click()} type="button">open it through a share link</button>, which serves it at the root.
      </p>
    </div>
  );
}

EntryPicker.propTypes = {
  entry: PropTypes.object,
  onApply: PropTypes.func,
  onSave: PropTypes.func,
  readOnly: PropTypes.bool,
  running: PropTypes.bool,
  // `port` null: a saved name alone.
  saved: PropTypes.shape({ service: PropTypes.string, port: PropTypes.number }),
};

/** One compose service: its state, Start / Stop while the stack runs, and its logs on demand. */
function ComposeItem({ sessionId, service, state = null, stackRunning, onChanged, readOnly, failure = null }) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [logs, setLogs] = useState(null);
  const running = state?.state === 'running';
  const act = async(action) => {
    setBusy(action);
    setError(null);
    try {
      await composeServiceAction(sessionId, service.name, action);
      onChanged();
    } catch (err) {
      setError(`Could not ${action} ${service.name}: ${err?.message || 'unknown error'}`);
    } finally {
      setBusy(null);
    }
  };
  const showLogs = async() => {
    if (logs) { setLogs(null); return; }
    try {
      setLogs(await getComposeLogs(sessionId, service.name));
    } catch (err) {
      setError(`No logs for ${service.name}: ${err?.message || 'unknown error'}`);
    }
  };
  const ports = state?.ports?.length ? state.ports.map(String) : service.ports || [];
  return (
    <li className="apps-compose-item" data-testid={`apps-compose-item-${service.name}`}>
      <div className="apps-compose-row">
        <span className="apps-found-text">
          <strong>{service.name}</strong>
          <span className="apps-muted apps-small">
            {[whatItRuns(service), ports.length ? `ports ${ports.join(', ')}` : null, service.dependsOn?.length ? `after ${service.dependsOn.join(', ')}` : null].filter(Boolean).join(' · ')}
          </span>
        </span>
        {failure && !stackRunning ? (
          <span className="apps-tag is-bad" data-testid={`apps-compose-state-${service.name}`} title={failure}>Failed · not created</span>
        ) : (
          <span className={`apps-tag${running ? ' is-accent' : ''}`} data-testid={`apps-compose-state-${service.name}`} title={state?.status || undefined}>{stackRunning ? stateLabel(state?.state) : 'Not started'}</span>
        )}
        {!readOnly && (
          running ? (
            <Button disabled={Boolean(busy)} loading={busy === 'stop'} onClick={() => act('stop')} size="small">Stop</Button>
          ) : (
            <Button disabled={!stackRunning || Boolean(busy)} loading={busy === 'start'} onClick={() => act('start')} size="small" title={stackRunning ? undefined : 'Start the stack first (Start all): it brings every service up'}>Start</Button>
          )
        )}
        <Button disabled={!stackRunning} onClick={showLogs} size="small">{logs ? 'Hide logs' : 'Logs'}</Button>
      </div>
      {error && <p className="apps-bad apps-small" role="alert">{error}</p>}
      {logs && <pre className="apps-compose-logs" data-testid={`apps-compose-logs-${service.name}`}>{logs.length ? logs.join('\n') : 'Nothing printed yet.'}</pre>}
    </li>
  );
}

ComposeItem.propTypes = {
  // Why the stack failed (it created no container): the row says so instead of "Not started".
  failure: PropTypes.string,
  onChanged: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  service: PropTypes.object.isRequired,
  sessionId: PropTypes.string.isRequired,
  stackRunning: PropTypes.bool,
  state: PropTypes.object,
};

export default function RunnableDetail({
  item, runner = null, sessionId, readOnly = false, pollMs = COMPOSE_POLL_MS, failure = null, entry = null, onSaveEntry = null, onApplyEntry = null,
  projectId = null, onProvided = null, provideRequest = 0,
}) {
  const stackRunning = isRunning(runner) && runner?.buildStatus !== 'building';
  const [states, setStates] = useState({});
  const missing = Array.isArray(item.missing) ? item.missing : [];
  const canProvide = Boolean(projectId) && !readOnly && missing.length > 0;
  const [providing, setProviding] = useState(false);
  // Asked from outside (a refused Start's "Provide the missing files"): opened once per request.
  useEffect(() => { if (provideRequest > 0 && canProvide) { setProviding(true); } }, [provideRequest, canProvide]);
  const problemsBox = (
    <>
      <FileProblems missing={missing} onProvide={canProvide ? () => setProviding(true) : null} problems={item.problems} provided={item.provided || []} warnings={item.warnings} />
      {canProvide && (
        <ProvideMissingFiles
          missing={missing}
          onClose={() => setProviding(false)}
          onSaved={onProvided}
          open={providing}
          projectId={projectId}
          service={item.service}
        />
      )}
    </>
  );
  // The entry point's candidates as the compose services API says them while the stack runs (fresher than `entry`).
  const [liveEntry, setLiveEntry] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (item.kind !== 'compose' || !stackRunning) { setStates({}); return undefined; }
    let live = true;
    const read = () => getComposeServices(sessionId)
      .then((answer) => {
        if (!live) { return; }
        setStates(Object.fromEntries((answer?.services || []).map((state) => [state.name, state])));
        setLiveEntry(answer?.entry || null);
      })
      .catch(() => {});
    read();
    const timer = setInterval(read, pollMs);
    return () => { live = false; clearInterval(timer); };
  }, [item.kind, sessionId, stackRunning, tick, pollMs]);

  if (item.kind === 'dockerfile') {
    return (
      <Card testId="apps-runnable-dockerfile" title="Dockerfile">
        {problemsBox}
        <p>Builds <code>{item.file}</code> from its folder (<code>{folderOf(item.file)}</code>) in <code>{item.service}</code> and runs the image; the preview goes to the port its <code>EXPOSE</code> names.</p>
      </Card>
    );
  }
  // The file's services; one the runner lists that the file no longer has still shows (it can be stopped).
  const own = item.services || [];
  const listed = [...own, ...Object.keys(states).filter((name) => !own.some((service) => service.name === name)).map((name) => ({ name, ports: [] }))];
  return (
    <section className="apps-card" data-testid="apps-runnable-compose">
      <h3 className="apps-compose-title">Services in <code>{item.file}</code></h3>
      <EntryPicker entry={liveEntry || entry} onApply={onApplyEntry} onSave={onSaveEntry} readOnly={readOnly} running={stackRunning} saved={item.entry || null} />
      {problemsBox}
      {failure && !stackRunning && <p className="apps-bad apps-small" data-testid="apps-compose-failure">The stack did not start, so none of its services was created: {failure}</p>}
      <p className="apps-muted apps-small">
        {stackRunning ? 'Each one can be stopped and started on its own while the stack runs.' : 'Start all starts the whole stack (docker compose up, from the file\'s folder); then each one can be stopped and started on its own.'}
      </p>
      {listed.length === 0 ? <p className="apps-muted">The file has no services.</p> : (
        <ul className="apps-compose-list">
          {listed.map((service) => (
            <ComposeItem
              failure={failure}
              key={service.name}
              onChanged={() => setTick((n) => n + 1)}
              readOnly={readOnly}
              service={service}
              sessionId={sessionId}
              stackRunning={stackRunning}
              state={states[service.name] || null}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

RunnableDetail.propTypes = {
  // The runner's preview entry point of a compose app (`composeEntry` on its status), and saving / applying a choice.
  entry: PropTypes.object,
  onApplyEntry: PropTypes.func,
  onSaveEntry: PropTypes.func,
  // The stack's problem (it failed or crashed), from the Problem box: its services read "Failed · not created".
  failure: PropTypes.string,
  item: PropTypes.shape({
    kind: PropTypes.string.isRequired, file: PropTypes.string.isRequired, service: PropTypes.string.isRequired, services: PropTypes.array, problems: PropTypes.array, warnings: PropTypes.array,
    missing: PropTypes.array, provided: PropTypes.array,
  }).isRequired,
  pollMs: PropTypes.number,
  // The project (for "Provide missing files"), and what to do once they are saved (read the runnables again).
  projectId: PropTypes.string,
  onProvided: PropTypes.func,
  // A new number opens "Provide missing files" (a refused Start asked for it).
  provideRequest: PropTypes.number,
  readOnly: PropTypes.bool,
  runner: PropTypes.object,
  sessionId: PropTypes.string.isRequired,
};
